import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Hotel, Plus, Edit, Trash2, MapPin, Building, Cloud, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import PMSConfigModal from './PMSConfigModal';
import PMSDetailsModal from './PMSDetailsModal';
import { getPMSStatus } from '../../services/pmsApi';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const HotelManagement = () => {
  const [brands, setBrands] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [destinationTypes, setDestinationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [newHotel, setNewHotel] = useState({
    name: '',
    city: '',
    country: '',
    destination_type: ''
  });
  const [creating, setCreating] = useState(false);
  const [isPMSModalOpen, setIsPMSModalOpen] = useState(false);
  const [isPMSDetailsModalOpen, setIsPMSDetailsModalOpen] = useState(false);
  const [selectedHotelForPMS, setSelectedHotelForPMS] = useState(null);
  const [pmsStatuses, setPmsStatuses] = useState({}); // { hotelId: { connected: bool, provider: string, ... } }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch brands
      const brandsResponse = await fetch('/api/tenants/brands', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      let brandsData = { brands: [] };
      if (brandsResponse.ok) {
        brandsData = await brandsResponse.json();
        setBrands(brandsData.brands || []);
      }

      // Fetch destination types
      const destResponse = await fetch('/api/tenants/destination-types', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      if (destResponse.ok) {
        const destData = await destResponse.json();
        setDestinationTypes(destData.destination_types || []);
      }

      // Fetch hotels for all brands
      const allHotels = [];
      for (const brand of brandsData.brands || []) {
        const hotelsResponse = await fetch(`/api/tenants/brands/${brand.id}/hotels`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
        });
        if (hotelsResponse.ok) {
          const hotelsData = await hotelsResponse.json();
          const hotelsWithBrand = (hotelsData.hotels || []).map(hotel => ({
            ...hotel,
            brand_name: brand.name,
            brand_id: brand.id
          }));
          allHotels.push(...hotelsWithBrand);
        }
      }
      setHotels(allHotels);
      
      // Fetch PMS status for all hotels
      await fetchPMSStatuses(allHotels);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPMSStatuses = async (hotelsList) => {
    const statuses = {};
    
    for (const hotel of hotelsList) {
      try {
        const status = await getPMSStatus(hotel.brand_id, hotel.id);
        statuses[hotel.id] = {
          connected: status.connected || false,
          provider: status.pms_provider,
          propertyId: status.pms_property_id,
          lastSync: status.last_sync,
          syncStatus: status.sync_status,
        };
      } catch (error) {
        // If error (likely 404), hotel is not connected
        statuses[hotel.id] = {
          connected: false,
        };
      }
    }
    
    setPmsStatuses(statuses);
  };

  const createHotel = async () => {
    if (!selectedBrand || !newHotel.name.trim()) return;
    
    setCreating(true);
    try {
      const response = await fetch(`/api/tenants/brands/${selectedBrand}/hotels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
        },
        body: JSON.stringify(newHotel),
      });
      
      if (response.ok) {
        const data = await response.json();
        const brand = brands.find(b => b.id === selectedBrand);
        const hotelWithBrand = {
          ...data.hotel,
          brand_name: brand?.name,
          brand_id: selectedBrand
        };
        setHotels([...hotels, hotelWithBrand]);
        setNewHotel({ name: '', city: '', country: '', destination_type: '' });
        setSelectedBrand('');
        setIsCreateModalOpen(false);
      } else {
        console.error('Failed to create hotel');
      }
    } catch (error) {
      console.error('Error creating hotel:', error);
    } finally {
      setCreating(false);
    }
  };

  const updateHotelDestination = async (hotelId, destinationType) => {
    try {
      const response = await fetch(`/api/tenants/hotels/${hotelId}/destination-type`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
        },
        body: JSON.stringify({ destination_type: destinationType }),
      });
      
      if (response.ok) {
        setHotels(hotels.map(hotel => 
          hotel.id === hotelId 
            ? { ...hotel, destination_type: destinationType }
            : hotel
        ));
      }
    } catch (error) {
      console.error('Error updating hotel destination:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleConnectPMS = (hotel) => {
    setSelectedHotelForPMS(hotel);
    setIsPMSModalOpen(true);
  };

  const handleViewPMSDetails = (hotel) => {
    setSelectedHotelForPMS(hotel);
    setIsPMSDetailsModalOpen(true);
  };

  const handlePMSConnectSuccess = (connectionData) => {
    console.log('PMS connected successfully:', connectionData);
    // Refresh hotel data to show updated PMS status
    fetchData();
  };

  const handlePMSDisconnect = () => {
    console.log('PMS disconnected');
    // Refresh hotel data to show updated PMS status
    fetchData();
  };

  const handleSyncSuccess = () => {
    console.log('Sync completed');
    // Refresh hotel data to show updated sync time
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading hotels...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Hotel Management</h2>
          <p className="text-muted-foreground">
            Manage your hotel properties and their configurations
          </p>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2" disabled={brands.length === 0}>
              <Plus className="h-4 w-4" />
              <span>Add Hotel</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Hotel</DialogTitle>
              <DialogDescription>
                Add a new hotel property to your brand
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="brand-select">Brand</Label>
                <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hotel-name">Hotel Name</Label>
                <Input
                  id="hotel-name"
                  placeholder="Enter hotel name"
                  value={newHotel.name}
                  onChange={(e) => setNewHotel({...newHotel, name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    placeholder="City"
                    value={newHotel.city}
                    onChange={(e) => setNewHotel({...newHotel, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    placeholder="Country"
                    value={newHotel.country}
                    onChange={(e) => setNewHotel({...newHotel, country: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="destination-type">Destination Type</Label>
                <Select 
                  value={newHotel.destination_type} 
                  onValueChange={(value) => setNewHotel({...newHotel, destination_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select destination type" />
                  </SelectTrigger>
                  <SelectContent>
                    {destinationTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createHotel} 
                disabled={creating || !selectedBrand || !newHotel.name.trim()}
              >
                {creating ? 'Creating...' : 'Create Hotel'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Hotels Grid */}
      {brands.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No brands available</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create a brand first before adding hotels
            </p>
          </CardContent>
        </Card>
      ) : hotels.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Hotel className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hotels yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Add your first hotel property to start managing room types and pricing
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Add Your First Hotel</span>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hotels.map((hotel) => (
            <Card key={hotel.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                      <Hotel className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{hotel.name}</CardTitle>
                      <CardDescription className="flex items-center space-x-1">
                        <MapPin className="h-3 w-3" />
                        <span>{hotel.city}, {hotel.country}</span>
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Brand:</span>
                  <Badge variant="secondary">{hotel.brand_name}</Badge>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Destination Type:</Label>
                  <Select 
                    value={hotel.destination_type || ''} 
                    onValueChange={(value) => updateHotelDestination(hotel.id, value)}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select destination type" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinationTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hotel.destination_type && (
                    <div className="text-xs text-green-600">
                      ✓ Saved: {destinationTypes.find(t => t.id === hotel.destination_type)?.name}
                    </div>
                  )}
                </div>

                {/* PMS Integration Status */}
                <div className="border-t pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm flex items-center space-x-1">
                      <Cloud className="h-3 w-3" />
                      <span>PMS Integration</span>
                    </Label>
                  </div>
                  
                  {/* PMS Status - Real data from API */}
                  {pmsStatuses[hotel.id]?.connected ? (
                    // Connected state
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <div>
                            <p className="text-xs font-medium text-green-900">
                              Connected to {pmsStatuses[hotel.id].provider || 'PMS'}
                            </p>
                            <p className="text-xs text-green-600">
                              {pmsStatuses[hotel.id].lastSync 
                                ? `Last sync: ${new Date(pmsStatuses[hotel.id].lastSync).toLocaleString()}`
                                : 'Never synced'}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-6 w-6 p-0"
                          title="PMS Settings"
                          onClick={() => handleViewPMSDetails(hotel)}
                        >
                          <SettingsIcon className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 h-7 text-xs"
                          onClick={() => handleViewPMSDetails(hotel)}
                        >
                          View Details
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 px-2"
                          title="Sync Now"
                          onClick={() => handleViewPMSDetails(hotel)}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Not connected state
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-md">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                          <p className="text-xs text-gray-600">Not Connected</p>
                        </div>
                      </div>
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="w-full h-7 text-xs"
                        onClick={() => handleConnectPMS(hotel)}
                      >
                        Connect PMS
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="text-xs text-muted-foreground">
                    Created {formatDate(hotel.created_at)}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* PMS Configuration Modal */}
      <PMSConfigModal
        isOpen={isPMSModalOpen}
        onClose={() => setIsPMSModalOpen(false)}
        hotel={selectedHotelForPMS}
        onSuccess={handlePMSConnectSuccess}
      />

      {/* PMS Details Modal */}
      <PMSDetailsModal
        isOpen={isPMSDetailsModalOpen}
        onClose={() => setIsPMSDetailsModalOpen(false)}
        hotel={selectedHotelForPMS}
        pmsStatus={selectedHotelForPMS ? pmsStatuses[selectedHotelForPMS.id] : null}
        onDisconnect={handlePMSDisconnect}
        onSyncSuccess={handleSyncSuccess}
      />
    </div>
  );
};

export default HotelManagement;
